export function stripProtocol(url: string) {
  return url.replace(/(^\w+:|^)\/\//, "");
}

export function isRelativeUrl(url: string) {
  return !url.includes("://") && !url.startsWith("//");
}

export function compressPath(path: string) {
  return path.replace(/^([^/]+\/)(?:[^/]+\/)+([^/]+)$/, "$1.../$2");
}

export function updateQueryStringParam(url: string, key: string, value: string) {
  const [baseUrl, urlQueryString] = url.split("?");
  const newParam = `${key}=${value}`;
  let params = `?${newParam}`;

  // If the "search" string exists, then build params from it
  if (urlQueryString) {
    const updateRegex = new RegExp(`([?&])${key}[^&]*`);
    const removeRegex = new RegExp(`([?&])${key}=[^&;]+[&;]?`);

    if (typeof value === "undefined" || value === null || value === "") {
      // Remove param if value is empty
      params = urlQueryString.replace(removeRegex, "$1");
      params = params.replace(/[&;]$/, "");
    } else if (urlQueryString.match(updateRegex) !== null) {
      // If param exists already, update it
      params = urlQueryString.replace(updateRegex, `$1${newParam}`);
    } else {
      // Otherwise, add it to end of query string
      params = `?${urlQueryString}&${newParam}`;
    }
  }

  // no parameter was set so we don't need the question mark
  params = params === "?" ? "" : params;

  return baseUrl + params;
}

export function getFileExtension(fileName: string) {
  return fileName.split(".").pop();
}

export function equalsIgnoringCase(s1?: string, s2?: string, useLocale = true) {
  if (s1 == null || s2 == null) return false;

  if (useLocale) {
    return s1.localeCompare(s2, "en", { sensitivity: "base" }) === 0;
  }

  if (s1.length !== s2.length) return false;
  return s1.toLowerCase() === s2.toLowerCase();
}

export function dashify(str: string) {
  return str
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    // eslint-disable-next-line regexp/no-obscure-range
    .replace(/\W/g, m => (/[À-ž]/.test(m) ? m : "-"))
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}

export function toKebabCase(str: string) {
  const KEBAB_REGEX = /[A-Z\u00C0-\u00D6\u00D8-\u00DE]/g;
  return str.replace(KEBAB_REGEX, match => `-${match.toLowerCase()}`);
}

export function removeDuplicateForwardSlashes(str: string) {
  return str.replace(/(^\/|[^:]\/)\/+/g, "$1");
}

export function isHttps(str: string) {
  return str.toLowerCase().startsWith("https://");
}

export function isHttp(str: string) {
  return str.toLowerCase().startsWith("http://");
}

const PROTOCOL_REGEX = /^(\w+):\/\//;
export function setHttpProtocolIfNeeded(url: string): string {
  return setProtocolIfNeeded(url, "http");
}

export function setHttpsProtocolIfNeeded(url: string): string {
  return setProtocolIfNeeded(url, "https");
}

export function setProtocolIfNeeded(url: string, newProtocol: string): string {
  const match = url.match(PROTOCOL_REGEX);
  if (match) return url;
  return `${newProtocol}://${url}`;
}

export function isNullOrEmpty(str: string | undefined | null) {
  return str === undefined || str === null || str.trim() === "";
}

export function parseBool(str: string) {
  if (isNullOrEmpty(str)) {
    return false;
  }

  return str.trim().toLowerCase() === "true";
}

// Remove dashes in string (mainly used for guids)
export function removeDashes(s: string): string {
  return s.replace(/-/g, "");
}

export function removeEmojis(str: string) {
  try {
    return str.replace(/(?![*#0-9])[\p{Emoji}\p{Emoji_Component}]/gu, "");
  } catch {
    return str;
  }
}

// Empty Guid in string format
export const EmptyGuid = "00000000-0000-0000-0000-000000000000";

const lut: string[] = [];
for (let i = 0; i < 256; i++) {
  lut[i] = (i < 16 ? "0" : "") + i.toString(16);
}

export function normalizeGuid(guid: string) {
  return guid.replaceAll("-", "").toLowerCase();
}

export function newGuid(): string {
  return crypto.randomUUID();
}

// Pseudo-generate a guid via js
export function guid(includeDashes?: boolean, isSecure?: boolean): string {
  const rVals = new Uint32Array(4);
  const hasCrypto = isSecure && window.crypto != null && window.crypto.getRandomValues != null;

  if (hasCrypto) {
    window.crypto.getRandomValues(rVals);
  }

  /* eslint-disable no-bitwise */
  const d0 = hasCrypto ? (rVals[0] * 0x100000000) >>> 0 : (Math.random() * 0xFFFFFFFF) | 0;
  const d1 = hasCrypto ? (rVals[1] * 0x100000000) >>> 0 : (Math.random() * 0xFFFFFFFF) | 0;
  const d2 = hasCrypto ? (rVals[2] * 0x100000000) >>> 0 : (Math.random() * 0xFFFFFFFF) | 0;
  const d3 = hasCrypto ? (rVals[3] * 0x100000000) >>> 0 : (Math.random() * 0xFFFFFFFF) | 0;

  return (
    lut[d0 & 0xFF]
    + lut[(d0 >> 8) & 0xFF]
    + lut[(d0 >> 16) & 0xFF]
    + lut[(d0 >> 24) & 0xFF]
    + (includeDashes ? "-" : "")
    + lut[d1 & 0xFF]
    + lut[(d1 >> 8) & 0xFF]
    + (includeDashes ? "-" : "")
    + lut[((d1 >> 16) & 0x0F) | 0x40]
    + lut[(d1 >> 24) & 0xFF]
    + (includeDashes ? "-" : "")
    + lut[(d2 & 0x3F) | 0x80]
    + lut[(d2 >> 8) & 0xFF]
    + (includeDashes ? "-" : "")
    + lut[(d2 >> 16) & 0xFF]
    + lut[(d2 >> 24) & 0xFF]
    + lut[d3 & 0xFF]
    + lut[(d3 >> 8) & 0xFF]
    + lut[(d3 >> 16) & 0xFF]
    + lut[(d3 >> 24) & 0xFF]
  );
  /* eslint-enable no-bitwise */
}

const byteToHex: string[] = [];

for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 0x100).toString(16).slice(1));
}

export function stringifyGuid(array: ArrayLike<number>): string {
  let hex = "";
  for (let i = 0; i < array.length; i++) {
    hex += byteToHex[array[i]!];
  }

  // eslint-disable-next-line prefer-template
  const result = hex.substring(6, 8)
    + hex.substring(4, 6)
    + hex.substring(2, 4)
    + hex.substring(0, 2)
    + "-"
    + hex.substring(10, 12)
    + hex.substring(8, 10)
    + "-"
    + hex.substring(14, 16)
    + hex.substring(12, 14)
    + "-"
    + hex.substring(16, 20)
    + "-"
    + hex.substring(20);

  return result;
}

export function getFilenameFromURL(url: string): string | undefined {
  try {
    const parsedUrl = new URL(url);
    const pathname = parsedUrl.pathname;
    const pathSegments = pathname.split("/");
    return pathSegments.pop();
  } catch (error) {
    console.error("Error parsing URL:", error);
    return undefined;
  }
}

export function removeParameterFromHash(hash: string, parameter: string) {
  if (hash.length === 0) return "";
  const prms = hash.replace("#", "").split("&");
  const frmElements = [];
  for (let i = 0; i < prms.length; i++) {
    const current = prms[i].split("=");
    if (current[0] === parameter) continue;
    frmElements.push(prms[i]);
  }
  const fragment = frmElements.join("&");
  return fragment.length > 0 ? `#${fragment}` : "";
}

export function getQueryVarString(href: string, query: string) {
  const qstring = href.split("?");
  if (qstring.length < 2) return "";
  return getParameter(qstring, query);
}

export function getHashVarString(href: string, query: string) {
  const qstring = href.split("#");
  if (qstring.length < 2) return "";
  return getParameter(qstring, query);
}

function getParameter(qstring: string[], query: string) {
  const prms = qstring[1].split("&");
  const frmelements: string[][] = [];
  let current;
  let querystr = "";

  for (let i = 0; i < prms.length; i++) {
    current = prms[i].split("=");
    frmelements[i] = [];
    frmelements[i][0] = current[0];
    frmelements[i][1] = current[1];
  }
  for (const frmelement of frmelements) {
    if (frmelement[0].toLowerCase() === query.toLowerCase()) {
      querystr = frmelement[1];
      break;
    }
  }

  return querystr;
}

export function uuid() {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
}

export function uuidDashify(str: string) {
  return `${str.substr(0, 8)}-${str.substr(8, 4)}-${str.substr(12, 4)}-${str.substr(16, 4)}-${str.substr(20)}`;
}

export const pluralize = (count: number, singular: string, plural: string) => (count === 1 ? singular : plural);

export function trimEmailDomain(email: string) {
  if (!email || email.length === 1) return email;

  // Strip everything after and including the '@'
  const atSymbolIndex = email.indexOf("@");
  if (atSymbolIndex !== -1) {
    return email.substring(0, atSymbolIndex);
  }
  return email;
}

export function upperFirst(str: string) {
  const firstChar = str[0];
  if (firstChar) {
    return firstChar.toUpperCase() + str.slice(1);
  }
  return str;
}

export function toSafeString(a: unknown) {
  if (typeof a === "number") {
    if (Number.isNaN(a) || a === Infinity || a === -Infinity) {
      return "";
    }
    return String(a);
  }
  if (typeof a === "string") {
    return a;
  }
  return "";
}

export function humanReadableByteSize(byteSize: number): string {
  const sizeSuffixes: string[] = ["B", "KB", "MB", "GB", "TB", "PB", "EB"];
  if (byteSize < 0) {
    return `-${humanReadableByteSize(-byteSize)}`;
  }
  if (byteSize === 0) {
    return "0 B";
  }

  const magnitude: number = Math.floor(Math.log(byteSize) / Math.log(1024));
  const adjustedSize: number = byteSize / 1024 ** magnitude;

  return `${adjustedSize.toFixed(2)} ${sizeSuffixes[magnitude]}`;
}
